import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

function GearIcon({ className }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M7.84 1.804A1 1 0 018.82 1h2.36a1 1 0 01.98.804l.331 1.652a6.993 6.993 0 011.929 1.115l1.598-.54a1 1 0 011.186.447l1.18 2.044a1 1 0 01-.205 1.251l-1.267 1.113a7.047 7.047 0 010 2.228l1.267 1.113a1 1 0 01.206 1.25l-1.18 2.045a1 1 0 01-1.187.447l-1.598-.54a6.993 6.993 0 01-1.929 1.115l-.33 1.652a1 1 0 01-.98.804H8.82a1 1 0 01-.98-.804l-.331-1.652a6.993 6.993 0 01-1.929-1.115l-1.598.54a1 1 0 01-1.186-.447l-1.18-2.044a1 1 0 01.205-1.251l1.267-1.114a7.05 7.05 0 010-2.227L1.821 7.773a1 1 0 01-.206-1.25l1.18-2.045a1 1 0 011.187-.447l1.598.54A6.992 6.992 0 017.51 3.456l.33-1.652zM10 13a3 3 0 100-6 3 3 0 000 6z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function RobotIcon({ className }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path d="M10 1.5a.75.75 0 01.75.75V4h3.5A2.75 2.75 0 0117 6.75v7.5A2.75 2.75 0 0114.25 17h-8.5A2.75 2.75 0 013 14.25v-7.5A2.75 2.75 0 015.75 4H9.25V2.25A.75.75 0 0110 1.5zM7.5 9a1.25 1.25 0 100 2.5A1.25 1.25 0 007.5 9zm5 0a1.25 1.25 0 100 2.5A1.25 1.25 0 0012.5 9zM7.75 14h4.5a.75.75 0 000-1.5h-4.5a.75.75 0 000 1.5z" />
    </svg>
  );
}

function ChatPanel({ messages, onSend, busy, error, agentContext, onAgentContextChange }) {
  const [open, setOpen] = useState(true);
  const [draft, setDraft] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const bottomRef = useRef(null);
  const listRef = useRef(null);
  const settingsRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, busy]);

  useEffect(() => {
    if (!settingsOpen) return undefined;
    const onDoc = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setSettingsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [settingsOpen]);

  const submit = (e) => {
    e?.preventDefault?.();
    const text = draft.trim();
    if (!text || busy) return;
    setDraft('');
    onSend(text);
  };

  const updateBlock = (id, patch) => {
    onAgentContextChange?.(
      (agentContext || []).map((b) => (b.id === id ? { ...b, ...patch } : b))
    );
  };

  const addBlock = () => {
    const id = `ctx-${Date.now()}`;
    onAgentContextChange?.([
      ...(agentContext || []),
      { id, title: 'Context', body: '' },
    ]);
  };

  const removeBlock = (id) => {
    if (id === 'rules') return;
    onAgentContextChange?.((agentContext || []).filter((b) => b.id !== id));
  };

  if (!open) {
    return (
      <aside className="w-10 flex-shrink-0 h-full border-l border-paper-line bg-paper-soft flex flex-col items-center pt-3">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={`p-1.5 rounded-[4px] text-ink-muted hover:bg-wash hover:text-ink ${
            busy ? 'text-ink' : ''
          }`}
          title="Show assistant"
          aria-label="Show assistant"
        >
          <RobotIcon className="w-4 h-4" />
        </button>
        {busy ? (
          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-ok animate-pulse" title="Thinking" />
        ) : null}
      </aside>
    );
  }

  return (
    <aside className="w-[320px] flex-shrink-0 h-full border-l border-paper-line bg-paper-soft flex flex-col min-h-0">
      <div className="px-4 pt-5 pb-3 border-b border-paper-line relative" ref={settingsRef}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-1 min-w-0">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="mt-0.5 p-1.5 rounded-[4px] flex-shrink-0 text-ink-muted hover:bg-wash hover:text-ink"
              title="Hide assistant"
              aria-label="Hide assistant"
            >
              <RobotIcon className="w-4 h-4" />
            </button>
            <div className="min-w-0">
              <div className="text-[13px] font-semibold tracking-tight text-ink">Assistant</div>
              <p className="text-[11px] text-ink-faint mt-0.5 leading-relaxed">
                Change filters, blacklists, templates, scrape prefs — same as the UI.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setSettingsOpen((v) => !v)}
            className={`mt-0.5 p-1.5 rounded-[4px] flex-shrink-0 ${
              settingsOpen
                ? 'bg-wash-active text-ink'
                : 'text-ink-muted hover:bg-wash hover:text-ink'
            }`}
            title="Agent context"
            aria-label="Edit agent context"
          >
            <GearIcon className="w-4 h-4" />
          </button>
        </div>

        {settingsOpen && (
          <div className="absolute right-3 left-3 top-full mt-1.5 z-50 bg-white border border-paper-line rounded-md shadow-popover p-3 max-h-[60vh] overflow-y-auto">
            <div className="text-[12px] font-medium text-ink mb-1">Agent context</div>
            <p className="text-[11px] text-ink-faint mb-3 leading-relaxed">
              Injected into the system prompt every turn. Edit Rules or add more blocks.
            </p>
            <div className="space-y-3">
              {(agentContext || []).map((block) => (
                <div key={block.id} className="border border-paper-line rounded-md p-2">
                  <div className="flex items-center gap-2 mb-1.5">
                    <input
                      value={block.title}
                      onChange={(e) => updateBlock(block.id, { title: e.target.value })}
                      className="flex-1 px-2 py-1 text-[12px] font-medium border border-paper-line rounded-md"
                      placeholder="Title"
                    />
                    {block.id !== 'rules' && (
                      <button
                        type="button"
                        onClick={() => removeBlock(block.id)}
                        className="text-[11px] text-ink-faint hover:text-ink"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <textarea
                    value={block.body}
                    onChange={(e) => updateBlock(block.id, { body: e.target.value })}
                    rows={3}
                    className="w-full px-2 py-1.5 text-[12px] font-mono border border-paper-line rounded-md bg-paper-soft resize-y leading-relaxed"
                    placeholder="Context for the agent…"
                  />
                  {block.id === 'rules' && (
                    <div className="mt-1 text-[10px] font-mono text-ink-faint">id: rules</div>
                  )}
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addBlock}
              className="mt-3 text-[12px] text-ink-muted hover:text-ink"
            >
              + Add context
            </button>
          </div>
        )}
      </div>

      <div ref={listRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3 min-h-0">
        {messages.length === 0 && (
          <div className="text-[12px] text-ink-faint leading-relaxed px-1 py-6">
            Try: “only show direct emails”, “blacklist spam@example.com”, “set PULL project to
            shipping outbound”, or “go to emails”.
          </div>
        )}
        {messages.map((m, i) => (
          <div
            key={`${m.role}-${i}`}
            className={`text-[12px] leading-relaxed ${
              m.role === 'user' ? 'text-ink' : 'text-ink'
            }`}
          >
            <div className="text-[10px] text-ink-faint mb-1">
              {m.role === 'user' ? 'You' : 'Assistant'}
            </div>
            {m.role === 'assistant' ? (
              <div className="assistant-md prose-chat">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
              </div>
            ) : (
              <div className="whitespace-pre-wrap">{m.content}</div>
            )}
            {m.note && (
              <div className="mt-1 text-[10px] text-ink-faint">{m.note}</div>
            )}
          </div>
        ))}
        {busy && (
          <div className="text-[11px] text-ink-faint animate-pulse">Thinking…</div>
        )}
        {error && (
          <div className="text-[11px] border border-paper-line bg-callout px-2 py-1.5 text-ink">
            {error}
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={submit} className="border-t border-paper-line p-3">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          rows={3}
          placeholder="Ask to change settings…"
          disabled={busy}
          className="w-full px-2.5 py-2 text-[12px] border border-paper-line rounded-md bg-white text-ink resize-none disabled:opacity-50"
        />
        <div className="mt-2 flex items-center justify-between">
          <span className="text-[10px] text-ink-faint">Enter to send · Shift+Enter newline</span>
          <button
            type="submit"
            disabled={busy || !draft.trim()}
            className="text-[12px] px-2.5 py-1 rounded-md bg-cta text-white hover:bg-cta-hover disabled:opacity-40"
          >
            Send
          </button>
        </div>
      </form>

      <style>{`
        .prose-chat p { margin: 0 0 0.5rem; }
        .prose-chat p:last-child { margin-bottom: 0; }
        .prose-chat ul, .prose-chat ol { margin: 0.25rem 0 0.5rem; padding-left: 1.1rem; }
        .prose-chat li { margin: 0.15rem 0; }
        .prose-chat code {
          font-family: var(--mono);
          font-size: 85%;
          color: #eb5757;
          background: rgba(135, 131, 120, 0.15);
          padding: 0.2em 0.4em;
          border-radius: 3px;
        }
        .prose-chat pre {
          background: #f9f8f7;
          border: 1px solid var(--line);
          padding: 0.5rem 0.65rem;
          border-radius: 6px;
          overflow-x: auto;
          margin: 0.35rem 0;
        }
        .prose-chat pre code {
          background: transparent;
          color: var(--ink);
          padding: 0;
          font-size: 13px;
        }
        .prose-chat strong { color: var(--ink); font-weight: 600; }
        .prose-chat a { color: var(--link); text-decoration: underline; text-underline-offset: 2px; }
        .prose-chat a:hover { color: var(--cta-hover); }
      `}</style>
    </aside>
  );
}

export default ChatPanel;
